import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface ErrorAction {
  label: string;
  to?: string;
  href?: string;
  onClick?: () => void;
  icon?: React.ReactNode;
}

interface ErrorPageLayoutProps {
  statusCode: string;
  title: string;
  description: string;
  details?: string;
  primaryAction?: ErrorAction;
  secondaryAction?: ErrorAction;
  children?: React.ReactNode;
}

function renderAction(action: ErrorAction | undefined, type: "primary" | "secondary") {
  if (!action) return null;

  const className = cn(
    "rounded-full px-6 py-2 text-base font-semibold transition-all",
    type === "primary"
      ? "bg-toctoc-orange text-white hover:bg-[#E14D04] focus-visible:ring-2 focus-visible:ring-toctoc-orange/60"
      : "border border-toctoc-gray-medium/20 bg-white/70 text-toctoc-black hover:border-toctoc-orange/40 hover:bg-toctoc-beige/60 focus-visible:ring-2 focus-visible:ring-toctoc-orange/30"
  );

  const content = (
    <span className="flex items-center gap-2">
      <span>{action.label}</span>
      {action.icon}
    </span>
  );

  if (action.to) {
    return (
      <Button asChild className={className} key={action.label}>
        <Link to={action.to}>{content}</Link>
      </Button>
    );
  }

  if (action.href) {
    return (
      <Button asChild className={className} key={action.label}>
        <a href={action.href} target="_blank" rel="noopener noreferrer">
          {content}
        </a>
      </Button>
    );
  }

  return (
    <Button key={action.label} className={className} onClick={action.onClick} type="button">
      {content}
    </Button>
  );
}

const ErrorPageLayout: React.FC<ErrorPageLayoutProps> = ({
  statusCode,
  title,
  description,
  details,
  primaryAction,
  secondaryAction,
  children,
}) => {
  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-white via-toctoc-beige/40 to-white text-toctoc-black">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-24 -left-20 h-72 w-72 rounded-full bg-toctoc-orange/15 blur-3xl" />
        <div className="absolute -bottom-32 -right-16 h-80 w-80 rounded-full bg-toctoc-blue/15 blur-3xl" />
        <div className="absolute inset-x-0 top-1/3 h-px bg-gradient-to-r from-transparent via-toctoc-orange/30 to-transparent" />
      </div>

      <div className="relative z-10 flex min-h-screen flex-col px-6 pb-16 pt-10 sm:px-10">
        <header className="flex justify-center">
          <Link to="/" className="inline-flex items-center gap-3 rounded-full bg-white/90 px-4 py-2 shadow-md shadow-toctoc-orange/10 backdrop-blur">
            <img
              src="/lovable-uploads/24e0e29e-3e10-43db-8be9-1cc4bc18ab2f.png"
              alt="Toc Toc - Voltar para a página inicial"
              className="h-10 w-auto"
              loading="lazy"
              decoding="async"
            />
            <span className="text-lg font-semibold tracking-tight text-toctoc-black">Toc Toc</span>
          </Link>
        </header>

        <main className="flex flex-1 flex-col items-center justify-center">
          <div className="w-full max-w-3xl text-center">
            <span className="mx-auto inline-flex items-center justify-center gap-2 rounded-full border border-toctoc-orange/30 bg-white/80 px-5 py-2 text-xs font-semibold uppercase tracking-[0.35em] text-toctoc-orange shadow-sm shadow-toctoc-orange/10">
              {statusCode} • Toc Toc
            </span>

            <h1 className="mt-8 text-4xl font-bold tracking-tight text-toctoc-black sm:text-5xl">
              {title}
            </h1>

            <p className="mt-4 text-lg text-toctoc-gray-medium sm:text-xl">
              {description}
            </p>

            {details ? (
              <p className="mt-3 text-base text-toctoc-gray-medium/90">
                {details}
              </p>
            ) : null}

            <div className="mt-8 flex flex-wrap justify-center gap-4">
              {renderAction(primaryAction, "primary")}
              {renderAction(secondaryAction, "secondary")}
            </div>

            {children ? <div className="mt-12">{children}</div> : null}
          </div>
        </main>
      </div>
    </div>
  );
};

export default ErrorPageLayout;
